import React, { useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Editor } from '../../../../components/molecules/Form/components/Editor';
import { useRefreshBlock } from '../../../BlockFetcher';
import { PostFormTypes, PostTypes } from '../Post.types';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { FileUpload, Form, SonForm, SubmitButton, useForm } from '../../../Form2';
import { HiddenInput } from '../../../Form2/elements/HiddenInput/HiddenInput';

type PostsEditorProps = {
  form: SonForm<PostFormTypes>;
  type: PostTypes;
  autoFocus?: boolean;
  uuid: string;
};

/**
 * Wrapper for the posts rich text editor
 */
export function PostsEditor({ form, type, autoFocus, uuid }: PostsEditorProps) {
  const { t } = useSonTranslation('Posts');
  const id = useHtmlValidId();
  const { delayedRefresh } = useRefreshBlock();
  const onSuccess = useCallback(
    ({ data: newPostId }: AxiosResponse<string>) => delayedRefresh(newPostId),
    [delayedRefresh],
  );

  const { params, formProps, fetchState } = useForm({ form, onSuccess });

  const editorID = `posts-${type}-form-${uuid}`; // this must be uuid for uniqueness (see TinyMCE autosave plugin!)
  const editorInput = params.getByName('content');
  const replyTo = params.findByName('replyTo');
  const postId = params.findByName('postId');
  const fileUpload = params.findByName('fileuploadparams');

  return (
    <div className="posts__editor">
      <Form
        key={form.url} // key needed here so form completely initializes new when form changes
        id={`post-create-form-${id}`}
        modifier="no-padding"
        {...formProps}
      >
        <Editor
          value={editorInput.value as string}
          name={editorInput.name}
          id={editorID}
          constraints={editorInput?.constraints}
          autoFocus={autoFocus}
        />
        {fileUpload
          ? <FileUpload
              headingLabel={t('default.attachedfiles.headline.selected')}
              // for BE reasons fileData params are packed into fileuploadparams
              param={fileUpload.params[0]}
              formRef={formProps.ref}
            />
          : null}
        {type === 'reply' && replyTo && <HiddenInput name="replyTo" value={replyTo.value} />}
        {postId && <HiddenInput name="postId" value={postId.value} />}
        <SubmitButton
          buttonLabel={t(`default.editor.save.${type}`)}
          messageSuccess={t('default.editor.status.success', { type: t(`default.type.${type}`) })}
          messageSending={t('default.editor.status.sending', { type: t(`default.type.${type}`) })}
          messageFail={t('default.editor.status.fail', { type: t(`default.type.${type}`) })}
          customErrorTypeMessages={
            {
              missingPost: t('default.editor.status.missingPost',
                { type: t(`default.missingPostErrorType.${type}`) }),
            }
          }
          fetchState={fetchState}
          active={fetchState.type !== 'loading' && fetchState.type !== 'success'}
        />
      </Form>
    </div>
  );
}
